import { z } from "zod";
import type { BindingTarget, PromptBinding, PromptContent, PromptField } from "./prompt-content.js";
import { canvasEdgeSchema, canvasGroupSchema, pointSchema, type CanvasEdge, type CanvasGroup, type Point } from "./studio.js";
import { canvasDocumentV3Schema, canvasNodeV3Schema, type CanvasDocumentV3, type CanvasNodeV3 } from "./studio-v3.js";

export interface CanvasClipboardV3 {
  schemaVersion: 3;
  nodes: CanvasNodeV3[];
  edges: CanvasEdge[];
  groups: CanvasGroup[];
  relativePositions: Record<string, Point>;
}
const clipboardSchema = z.strictObject({
  schemaVersion: z.literal(3), nodes: z.array(canvasNodeV3Schema).max(5_000),
  edges: z.array(canvasEdgeSchema).max(10_000), groups: z.array(canvasGroupSchema).max(1_000),
  relativePositions: z.record(z.uuid(), pointSchema)
});

function promptFields(node: CanvasNodeV3): Array<[PromptField, PromptContent]> {
  if (node.kind === "text") return [["text", node.text]];
  if (node.kind === "script") return [["title", node.title], ["synopsis", node.synopsis]];
  if (node.kind === "seedance") return [["prompt", node.settings.prompt]];
  return [];
}
const slotKey = (nodeId: string, field: PromptField, slotId: string): string => JSON.stringify([nodeId, field, slotId]);

/** Select internal graph objects only. External binding identities remain diagnostic provenance until paste. */
export function selectCanvasSubgraphV3(document: CanvasDocumentV3, nodeIds: readonly string[]): CanvasClipboardV3 {
  canvasDocumentV3Schema.parse(document);
  const selected = new Set(nodeIds);
  const nodes = document.nodes.filter((node) => selected.has(node.id));
  const ids = new Set(nodes.map((node) => node.id));
  const origin = nodes.reduce((point, node) => ({ x: Math.min(point.x, node.position.x), y: Math.min(point.y, node.position.y) }), { x: Infinity, y: Infinity });
  return structuredClone({
    schemaVersion: 3, nodes,
    edges: document.edges.filter((edge) => ids.has(edge.sourceNodeId) && ids.has(edge.targetNodeId)),
    groups: document.groups.filter((group) => group.nodeIds.length > 0 && group.nodeIds.every((id) => ids.has(id))),
    relativePositions: Object.fromEntries(nodes.map((node) => [node.id, { x: node.position.x - origin.x, y: node.position.y - origin.y }]))
  });
}

/** Remaps only the clipboard. Destination IDs are used solely to avoid collisions, never to repair bindings. */
export function pasteCanvasSubgraphV3(document: CanvasDocumentV3, clipboard: CanvasClipboardV3, anchor: Point, nextId: () => string): {
  document: CanvasDocumentV3;
  inserted: { nodeIds: string[]; edgeIds: string[]; groupIds: string[] };
} {
  canvasDocumentV3Schema.parse(document);
  const copy = clipboardSchema.parse(clipboard);
  pointSchema.parse(anchor);
  if (copy.edges.some((edge) => edge.sourceNodeId === edge.targetNodeId)) throw new Error("复制子图不能包含自连接。");
  const copiedNodes = new Set(copy.nodes.map((node) => node.id));
  const copiedEdges = new Set(copy.edges.map((edge) => edge.id));
  if (Object.keys(copy.relativePositions).length !== copiedNodes.size || Object.keys(copy.relativePositions).some((id) => !copiedNodes.has(id)) || copy.nodes.some((node) => !copy.relativePositions[node.id])) throw new Error("复制子图的相对位置不完整。");

  // Remove absent prompt contexts, and turn excluded bound media into explicit
  // missing values BEFORE validation/remapping. Internal forged tuples still fail.
  for (const node of copy.nodes) {
    if (node.kind !== "seedance") continue;
    node.referenceBindings = node.referenceBindings.flatMap((binding): PromptBinding[] => {
      if (!copiedNodes.has(binding.source.nodeId) || (binding.source.inputEdgeId !== null && !copiedEdges.has(binding.source.inputEdgeId))) return [];
      if (binding.value.state === "bound" && (!copiedNodes.has(binding.value.target.nodeId) || !copiedEdges.has(binding.value.target.edgeId))) {
        return [{ ...binding, value: { state: "missing", reason: "not_copied", previous: binding.value.target } }];
      }
      return [binding];
    });
  }
  canvasDocumentV3Schema.parse({ schemaVersion: 3, viewport: { x: 0, y: 0, zoom: 1 }, nodes: copy.nodes, edges: copy.edges, groups: copy.groups });
  const inserted = { nodeIds: [] as string[], edgeIds: [] as string[], groupIds: [] as string[] };
  if (copy.nodes.length === 0) return { document, inserted };

  const occupied = new Set<string>();
  for (const graph of [document, copy]) {
    for (const collection of [graph.nodes, graph.edges, graph.groups]) for (const item of collection) occupied.add(item.id);
    for (const node of graph.nodes) for (const [, prompt] of promptFields(node)) {
      for (const segment of prompt.segments) if (segment.type === "reference") occupied.add(segment.slotId);
    }
  }
  const allocate = (): string => {
    // An injected or broken allocator must fail atomically, not loop forever.
    for (let attempt = 0; attempt < 1_024; attempt++) {
      const id = z.uuid().parse(nextId());
      if (occupied.has(id)) continue;
      occupied.add(id);
      return id;
    }
    throw new Error("无法分配不重复的新 ID，请重试复制。");
  };
  // Build all maps before constructing any remapped graph objects.
  const nodeMap = new Map(copy.nodes.map((node) => [node.id, allocate()]));
  const edgeMap = new Map(copy.edges.map((edge) => [edge.id, allocate()]));
  const slotMap = new Map<string, string>();
  for (const node of copy.nodes) for (const [field, prompt] of promptFields(node)) {
    for (const segment of prompt.segments) if (segment.type === "reference") {
      const key = slotKey(node.id, field, segment.slotId);
      if (!slotMap.has(key)) slotMap.set(key, allocate());
    }
  }
  const groupMap = new Map(copy.groups.map((group) => [group.id, allocate()]));
  // Project the original deterministic incoming sequence before changing UUIDs.
  // Unique positions ensure tied old orders cannot reorder media or shared text.
  const incomingCounts = new Map<string, number>();
  const copiedOrders = new Map<string, number>();
  for (const edge of [...copy.edges].sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))) {
    const order = incomingCounts.get(edge.targetNodeId) ?? 0;
    copiedOrders.set(edge.id, order);
    incomingCounts.set(edge.targetNodeId, order + 1);
  }
  const remapIdentity = (identity: BindingTarget): BindingTarget => ({
    ...identity, edgeId: edgeMap.get(identity.edgeId) ?? identity.edgeId, nodeId: nodeMap.get(identity.nodeId) ?? identity.nodeId
  });
  const nodes = copy.nodes.map((node) => {
    const originalId = node.id;
    for (const [field, prompt] of promptFields(node)) for (const segment of prompt.segments) {
      if (segment.type === "reference") segment.slotId = slotMap.get(slotKey(originalId, field, segment.slotId))!;
    }
    if (node.kind === "seedance") node.referenceBindings = node.referenceBindings.map((binding): PromptBinding => ({
      source: { ...binding.source, nodeId: nodeMap.get(binding.source.nodeId)!, inputEdgeId: binding.source.inputEdgeId === null ? null : edgeMap.get(binding.source.inputEdgeId)! },
      slotId: slotMap.get(slotKey(binding.source.nodeId, binding.source.field, binding.slotId))!,
      value: binding.value.state === "bound" ? { state: "bound", target: remapIdentity(binding.value.target) }
        : { ...binding.value, ...(binding.value.previous === undefined ? {} : { previous: remapIdentity(binding.value.previous) }) }
    }));
    const position = copy.relativePositions[originalId]!;
    return { ...node, id: nodeMap.get(originalId)!, position: { x: anchor.x + position.x, y: anchor.y + position.y } };
  });
  const edges = copy.edges.map((edge) => ({ ...edge, id: edgeMap.get(edge.id)!, sourceNodeId: nodeMap.get(edge.sourceNodeId)!, targetNodeId: nodeMap.get(edge.targetNodeId)!, order: copiedOrders.get(edge.id)! }));
  const groups = copy.groups.map((group) => ({ ...group, id: groupMap.get(group.id)!, nodeIds: group.nodeIds.map((id) => nodeMap.get(id)!) }));
  return {
    document: canvasDocumentV3Schema.parse({ ...document, nodes: [...document.nodes, ...nodes], edges: [...document.edges, ...edges], groups: [...document.groups, ...groups] }),
    inserted: { nodeIds: nodes.map((node) => node.id), edgeIds: edges.map((edge) => edge.id), groupIds: groups.map((group) => group.id) }
  };
}
